import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { closeTopForm } from '../utils/formStack';
import { backTarget, mainPath } from '../utils/backStep';
import { useAuth } from '../contexts/AuthContext';

// Keyboard "one step back", active on EVERY page (desktop/web counterpart of the
// Android hardware back button in BackButtonHandler.jsx). Both keys behave the
// same way:
//   • Escape    — always (a dropdown that is OPEN swallows it itself)
//   • Backspace — only when the caret is NOT in a text field (there it deletes)
// and each press goes back exactly one level:
//   1) an open form / modal / the menu drawer → close it and return to whatever
//      is underneath (a nested form returns to its parent form). Typed content
//      is NOT cleared.
//   2) we navigated INTO this page inside the app (menu, dashboard tile, row
//      drill-in) → navigate back one history step;
//   3) a sub-page with no in-app step behind it (Label setup, one service)
//      → its parent page (Print Labels, Services), and from there on →
//   4) no in-app step to go back to (the page was reloaded, opened from a link,
//      or the dev auto-reload fired) → go to the MAIN page: the Dashboard for
//      admins, Guests for members; sign-up / forgot password / OTP → Sign in;
//   5) already on the main page (or Sign in, or the guest screen) → nothing.
// Depth is counted from the router's own PUSH/POP events, so a page reached by
// clicks goes back the way it came; the main page is the floor.

// Where a Backspace must keep its normal "delete" meaning.
function isTypingTarget(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  // Buttons/checkboxes swallow no text, so Backspace there means "go back".
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes((el.type || 'text').toLowerCase());
}

export default function BackKeyHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const auth = useAuth();
  const { user, isAdmin } = auth;
  const depth = useRef(0);
  const here = useRef(location.pathname);
  here.current = location.pathname;
  // The main page of whoever is signed in (ONE rule, utils/backStep).
  const home = mainPath({ signedIn: !!user, isAdmin, canGuests: auth.can('CanMembers') });
  const homeRef = useRef(home);
  homeRef.current = home;

  // Track how many in-app steps we can pop. Login/logout use replace, so they
  // never count; a POP (browser back or our own navigate(-1)) unwinds one.
  useEffect(() => {
    if (navType === 'PUSH') depth.current += 1;
    else if (navType === 'POP') depth.current = Math.max(0, depth.current - 1);
  }, [location.key, navType]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const el = document.activeElement;
      if (e.key === 'Backspace') {
        if (isTypingTarget(el)) return;      // let it delete text
      } else if (e.key !== 'Escape') {
        return;
      }
      if (closeTopForm()) { e.preventDefault(); return; }

      // The same ladder the Android button climbs (utils/backStep); a browser
      // tab is not ours to close, so the main page is simply the floor here.
      const go = backTarget({ path: here.current, depth: depth.current, main: homeRef.current });
      if (go === null) { if (e.key === 'Backspace') e.preventDefault(); return; }

      // A filled input keeps its text; the key simply leaves the page.
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) el.blur();
      e.preventDefault();
      if (go === -1) navigate(-1);
      else navigate(go, { replace: true }); // '/app' lands on the main page (App.jsx <Home>)
    };
    window.addEventListener('keydown', onKey);

    // An embedded PDF preview (Print Labels, Share / Export) keeps every key
    // once it is clicked, so Esc would never reach us. Hand the focus back to
    // the page shortly after: the click inside the viewer has finished by
    // then, and the mouse wheel still scrolls the PDF without focus.
    let refocus;
    const onBlur = () => {
      clearTimeout(refocus);
      refocus = setTimeout(() => {
        const el = document.activeElement;
        if (el && el.tagName === 'IFRAME') { el.blur(); window.focus(); }
      }, 400);
    };
    window.addEventListener('blur', onBlur);
    return () => {
      clearTimeout(refocus);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, [navigate]);

  return null;
}
